import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/cn";
import { Image } from "../../primitives/Image";

/**
 * Media - one image, video, or audio clip with an optional caption. Renderer
 * for the K2 `media` block (`docs/contracts/block-registry.md`): the platform's
 * chat `BlockRenderer` passes a `media` block's `props` straight through, so the
 * prop names match the block payload exactly.
 *
 * Block props (`{ kind, src, alt?, caption?, poster?, width?, height? }`):
 *   - `kind` - `"image"` | `"video"` | `"audio"`. Selects the element.
 *   - `src` - the resource URL. Required.
 *   - `alt?` - alternative text (images) / accessible label (video/audio).
 *   - `caption?` - a short caption rendered under the media in a `<figcaption>`.
 *   - `poster?` - poster frame for `video`.
 *   - `width?` / `height?` - intrinsic pixel dimensions; used to reserve space
 *     (CLS prevention) via an aspect ratio when both are present.
 *
 * Client-only props (never part of the streamed block): `className`.
 *
 * Images go through the `Image` primitive (lazy-load, skeleton, error chrome).
 * Video and audio use the native elements with `controls` + `preload="metadata"`
 * so a clip never auto-downloads its whole stream. Lifted from tool-ui's
 * Image / Video / Audio schemas (MIT) + OpenAI Apps SDK Video, re-authored to
 * this repo's conventions. Token-themed (`border-border`, `bg-card`, ...) with
 * no hardcoded colors. SSR-safe: native elements render without JS; no
 * `window` / `document` access at module top or render.
 */

/** Source: tool-ui Image / Video / Audio, OpenAI Apps SDK Video. */
export type MediaKind = "image" | "video" | "audio";

const mediaVariants = cva("not-prose", {
  variants: {
    kind: {
      image: "",
      video: "",
      audio: "",
    },
  },
  defaultVariants: { kind: "image" },
});

export interface MediaProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "children">, VariantProps<typeof mediaVariants> {
  /** The media kind. Selects the element. Required. */
  kind: MediaKind;
  /** Resource URL. Required. */
  src: string;
  /** Alt text (image) / accessible label (video/audio). */
  alt?: string;
  /** Short caption rendered under the media. */
  caption?: string;
  /** Poster frame for `video`. */
  poster?: string;
  /** Intrinsic width in px (with `height`, reserves aspect-ratio space). */
  width?: number;
  /** Intrinsic height in px (with `width`, reserves aspect-ratio space). */
  height?: number;
}

export const Media = React.forwardRef<HTMLElement, MediaProps>(function Media(
  { className, kind, src, alt, caption, poster, width, height, ...rest },
  ref,
) {
  const aspectRatio =
    width && height && width > 0 && height > 0 ? `${width} / ${height}` : undefined;

  let body: React.ReactNode;
  if (kind === "video") {
    body = (
      <video
        src={src}
        poster={poster}
        controls
        preload="metadata"
        aria-label={alt || caption || "Video"}
        className="block w-full rounded-2xl border border-border/70 bg-black"
        style={aspectRatio ? { aspectRatio } : undefined}
        data-slot="media-video"
      />
    );
  } else if (kind === "audio") {
    body = (
      <audio
        src={src}
        controls
        preload="metadata"
        aria-label={alt || caption || "Audio"}
        className="block w-full"
        data-slot="media-audio"
      />
    );
  } else {
    body = (
      <Image
        src={src}
        alt={alt ?? ""}
        aspectRatio={aspectRatio}
        // The Image primitive's radius scale tops out at `lg` (12px); the chat
        // surface radius is 20px, so the wrapper overrides via `rounded-2xl`
        // (twMerge wins) and the inner <img> inherits it (`rounded-[inherit]`).
        rounded="none"
        className="w-full rounded-2xl border border-border/70"
        data-slot="media-image"
      />
    );
  }

  return (
    <figure
      ref={ref as React.Ref<HTMLElement>}
      className={cn(mediaVariants({ kind }), className)}
      data-slot="media"
      data-kind={kind}
      {...rest}
    >
      {body}
      {caption ? (
        <figcaption className="mt-2.5 px-1 text-xs leading-relaxed text-muted-foreground">
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
});

Media.displayName = "Media";

export { mediaVariants };
export type MediaVariantProps = VariantProps<typeof mediaVariants>;
